import * as React from 'react';
import { getDefaultDateRange } from 'utils/reportUtils';

export interface UseCalendarProps {
    defaultStartDate?: string;
    defaultEndDate?: string;
}

export interface UseCalendarReturn {
    // State
    currentDate: Date;
    selectedStartDate: string | null;
    selectedEndDate: string | null;
    
    // Actions
    setCurrentDate: React.Dispatch<React.SetStateAction<Date>>;
    handleDayClick: (selectedDay: Date) => void;
    navigateMonth: (direction: 'prev' | 'next') => void;
    
    // Computed values
    dateRangeText: string;
    isValidRange: boolean;
    
    // Calendar rendering helpers
    getCalendarDays: () => CalendarDay[];
}

export interface CalendarDay {
    date: Date;
    dayNumber: number;
    isSelected: boolean;
    isInRange: boolean;
    isStart: boolean;
    isEnd: boolean;
    isFuture: boolean;
    isToday: boolean;
    isEmpty: boolean; // For empty cells at start of month
}

export function useCalendar(props?: UseCalendarProps): UseCalendarReturn {
    const [currentDate, setCurrentDate] = React.useState(new Date());
    
    // Initialize with default range or provided values
    const [selectedStartDate, setSelectedStartDate] = React.useState<string | null>(() => {
        if (props?.defaultStartDate) return props.defaultStartDate;
        const defaultRange = getDefaultDateRange();
        return defaultRange.start;
    });
    
    const [selectedEndDate, setSelectedEndDate] = React.useState<string | null>(() => {
        if (props?.defaultEndDate) return props.defaultEndDate;
        const defaultRange = getDefaultDateRange();
        return defaultRange.end;
    });

    const handleDayClick = React.useCallback((selectedDay: Date) => {
        const dayString = selectedDay.toLocaleDateString("en-US");

        if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
            // Start new selection or reset existing range
            setSelectedStartDate(dayString);
            setSelectedEndDate(null);
        } else {
            // Set end date, ensuring chronological order
            if (new Date(selectedDay) < new Date(selectedStartDate)) {
                setSelectedEndDate(selectedStartDate);
                setSelectedStartDate(dayString);
            } else {
                setSelectedEndDate(dayString);
            }
        }
    }, [selectedStartDate, selectedEndDate]);

    const navigateMonth = React.useCallback((direction: 'prev' | 'next') => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            const monthOffset = direction === 'prev' ? -1 : 1;
            newDate.setMonth(newDate.getMonth() + monthOffset);
            return newDate;
        });
    }, []);

    const dateRangeText = React.useMemo(() => {
        if (selectedStartDate && selectedEndDate) {
            return `${selectedStartDate} - ${selectedEndDate}`;
        } else if (selectedStartDate) {
            return selectedStartDate;
        } else {
            return '';
        }
    }, [selectedStartDate, selectedEndDate]);

    const isValidRange = React.useMemo(() => {
        return !!(selectedStartDate && selectedEndDate);
    }, [selectedStartDate, selectedEndDate]);

    const getCalendarDays = React.useCallback((): CalendarDay[] => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysArray: CalendarDay[] = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfMonth; i++) {
            daysArray.push({
                date: new Date(), // Placeholder date
                dayNumber: 0,
                isSelected: false,
                isInRange: false,
                isStart: false,
                isEnd: false,
                isFuture: false,
                isToday: false,
                isEmpty: true
            });
        }

        // Add all days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            const day = new Date(year, month, i);
            const dayString = day.toLocaleDateString("en-US");
            
            const isStart = selectedStartDate === dayString;
            const isEnd = selectedEndDate === dayString;
            const isSelected = isStart || isEnd;
            const isInRange = !!(selectedStartDate && selectedEndDate &&
                new Date(day) > new Date(selectedStartDate) &&
                new Date(day) < new Date(selectedEndDate));
            
            const isFuture = day > today;
            const isToday = day.getTime() === today.getTime();

            daysArray.push({
                date: day,
                dayNumber: i,
                isSelected,
                isInRange,
                isStart,
                isEnd,
                isFuture,
                isToday,
                isEmpty: false
            });
        }

        return daysArray;
    }, [currentDate, selectedStartDate, selectedEndDate]);

    return {
        // State
        currentDate,
        selectedStartDate,
        selectedEndDate,
        
        // Actions
        setCurrentDate,
        handleDayClick,
        navigateMonth,
        
        // Computed values
        dateRangeText,
        isValidRange,
        
        // Calendar rendering helpers
        getCalendarDays
    };
}